import { useMemo } from 'react';

import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { PaginationHandler, PaginationInfo } from '@occc-app/hooks/usePaginatedQuery';

import {
  MISSING_STORES_SEARCH_PARAMS,
  REIMBURSEMENTS_SEARCH_PARAMS,
  STORES_SUBMITTED_SEARCH_PARAMS,
  TRANSACTIONS_REPORTED_SEARCH_PARAMS,
} from '../scan-data-overview.constants';
import { MetricType } from '../scan-data-overview.types';

export type UseScanDataStoreDetailsTableSearchAndPaginationReturn = {
  searchValueByMetric: Record<MetricType, string>;
  searchSetterByMetric: Record<MetricType, (value: string | undefined) => void>;
  paginationInfoByMetric: Record<MetricType, PaginationInfo>;
  pageChangeHandlersByMetric: Record<MetricType, PaginationHandler>;
};

const PAGE_SIZE = 20;

export const useScanDataStoreDetailsTableSearchAndPagination =
  (): UseScanDataStoreDetailsTableSearchAndPaginationReturn => {
    const [storesSubmittedSearch, setStoresSubmittedSearch] = useSearchParams(
      STORES_SUBMITTED_SEARCH_PARAMS,
    );
    const [missingStoresSearch, setMissingStoresSearch] = useSearchParams(
      MISSING_STORES_SEARCH_PARAMS,
    );
    const [transactionsReportedSearch, setTransactionsReportedSearch] = useSearchParams(
      TRANSACTIONS_REPORTED_SEARCH_PARAMS,
    );
    const [reimbursementsSearch, setReimbursementsSearch] = useSearchParams(
      REIMBURSEMENTS_SEARCH_PARAMS,
    );

    const [storesSubmittedPaginationInfo, onStoresSubmittedPageChange] = usePaginationParams({
      afterParamName: STORES_SUBMITTED_SEARCH_PARAMS.afterParamName,
      beforeParamName: STORES_SUBMITTED_SEARCH_PARAMS.beforeParamName,
      pageSize: PAGE_SIZE,
    });
    const [missingStoresPaginationInfo, onMissingStoresPageChange] = usePaginationParams({
      afterParamName: MISSING_STORES_SEARCH_PARAMS.afterParamName,
      beforeParamName: MISSING_STORES_SEARCH_PARAMS.beforeParamName,
      pageSize: PAGE_SIZE,
    });
    const [transactionsReportedPaginationInfo, onTransactionsReportedPageChange] =
      usePaginationParams({
        afterParamName: TRANSACTIONS_REPORTED_SEARCH_PARAMS.afterParamName,
        beforeParamName: TRANSACTIONS_REPORTED_SEARCH_PARAMS.beforeParamName,
        pageSize: PAGE_SIZE,
      });
    const [reimbursementsPaginationInfo, onReimbursementsPageChange] = usePaginationParams({
      afterParamName: REIMBURSEMENTS_SEARCH_PARAMS.afterParamName,
      beforeParamName: REIMBURSEMENTS_SEARCH_PARAMS.beforeParamName,
      pageSize: PAGE_SIZE,
    });

    const searchValueByMetric = useMemo(
      () => ({
        'stores-submitted': storesSubmittedSearch ?? '',
        'missing-stores': missingStoresSearch ?? '',
        'transactions-reported': transactionsReportedSearch ?? '',
        reimbursements: reimbursementsSearch ?? '',
      }),
      [
        storesSubmittedSearch,
        missingStoresSearch,
        transactionsReportedSearch,
        reimbursementsSearch,
      ],
    );

    const searchSetterByMetric = useMemo(
      () => ({
        'stores-submitted': setStoresSubmittedSearch,
        'missing-stores': setMissingStoresSearch,
        'transactions-reported': setTransactionsReportedSearch,
        reimbursements: setReimbursementsSearch,
      }),
      [
        setStoresSubmittedSearch,
        setMissingStoresSearch,
        setTransactionsReportedSearch,
        setReimbursementsSearch,
      ],
    );

    const paginationInfoByMetric: Record<MetricType, PaginationInfo> = {
      'stores-submitted': storesSubmittedPaginationInfo,
      'missing-stores': missingStoresPaginationInfo,
      'transactions-reported': transactionsReportedPaginationInfo,
      reimbursements: reimbursementsPaginationInfo,
    };

    const pageChangeHandlersByMetric: Record<MetricType, PaginationHandler> = {
      'stores-submitted': onStoresSubmittedPageChange,
      'missing-stores': onMissingStoresPageChange,
      'transactions-reported': onTransactionsReportedPageChange,
      reimbursements: onReimbursementsPageChange,
    };

    return {
      searchValueByMetric,
      searchSetterByMetric,
      paginationInfoByMetric,
      pageChangeHandlersByMetric,
    };
  };
